import axios from "axios";
import { toast } from "react-toastify";
import {
  createTelemetryAxiosInterceptor,
  getLogger,
} from "@hoteltradergit/ht-telemetry-ui-lib";
import { createAuthAxiosInterceptor } from "@hoteltradergit/auth";
import { getDecodedToken, getLoggedInUserEmail } from "../utils/tokenAccessor";

const DOWNLOAD_FILE = "DOWNLOAD_FILE/";

// The header the telemetry lib stamps the request's correlation id onto, and the
// key it files that id under in the log context. Mirrored here rather than
// imported: the lib keeps it internal to its axios/Rapid7 wiring and does not
// re-export it from the package entry.
const CORRELATION_ID_HEADER = "x-correlation-id";

// Identity for the API logs is best-effort. jwtDecode throws on a missing or
// malformed token, and this used to run *inside* handleRequest's try block —
// so a logged-out or half-restored session took the whole request down before
// axios was ever called, then rethrew from the catch on `response.status`.
// A request must never fail because we couldn't name the user.
const readUserId = () => {
  try {
    return getLoggedInUserEmail() || undefined;
  } catch {
    return undefined;
  }
};

// Decode the stored idToken and map its claims to the UAL actor headers (EO-402).
// Returns an empty object when the token is missing or malformed so the
// interceptor never throws on requests made before/without authentication.
const getActorHeaders = () => {
  try {
    const claims = getDecodedToken();
    if (!claims) {
      return {};
    }
    return {
      "X-User-Id": claims["custom:userId"] ?? "",
      "X-User-Name": claims["custom:username"] ?? "",
      "X-User-Email": claims.email ?? "",
      "X-User-Role": claims["custom:userRole"] ?? "",
    };
  } catch {
    return {};
  }
};

const createAxiosInstance = (baseURL) => {
  const HEADERS = {
    "Content-Type": "application/json",
  };

  const FILE_HEADERS = {
    "Content-Type": "multipart/form-data",
  };

  const axiosInstance = axios.create({
    headers: HEADERS,
    baseURL,
  });

  // Authorization: Bearer, X-App-ID and 401 → /logout are owned by
  // @hoteltradergit/auth. The lib reads the id token from a
  // module-scoped registry populated by <AuthProvider> on each render, not
  // localStorage — which flips true before ROCP flushes its localStorage
  // write, so a raw localStorage.getItem("ROCP_idToken") read here sent an
  // empty `Bearer ` on the first requests after login/reload. Its 401 handler
  // redirects to /logout where the mounted <LogoutHandler> runs the canonical
  // logout flow (Heap reset, storage clear, cross-tab broadcast, Cognito
  // /logout). Registered first so DOWNLOAD_FILE rewrite and telemetry below
  // run *before* it — auth headers are appended last, after the URL is final.
  createAuthAxiosInterceptor(axiosInstance);

  // Supplier-specific request rewrites layered on top of the auth interceptor:
  // - DOWNLOAD_FILE/* → strip prefix and switch to a blob response. The prefix
  //   is a client-side convention and must never leave the browser.
  // - UAL actor headers (EO-402) from the id token. Bearer, X-App-ID and
  //   x-correlation-id are owned by the auth and telemetry interceptors above,
  //   so they are deliberately not set here.
  axiosInstance.interceptors.request.use(
    (config) => {
      Object.assign(config.headers, getActorHeaders());

      if (config.url.startsWith(DOWNLOAD_FILE)) {
        config.url = config.url.replace(DOWNLOAD_FILE, "");
        config.responseType = "blob";
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // API telemetry (api.request / api.response / api.error) comes from the shared
  // lib rather than being hand-rolled here, so the log shape stays in step with
  // the other HT frontends and PII redaction of the payloads is the lib's job.
  //
  // `getLogger` is passed as a factory, not called: this module is imported via
  // App *before* index.jsx's body runs configureHotelTraderTelemetry, so
  // `getLogger()` here would capture the pre-config no-op logger for the
  // lifetime of the app. The lib resolves the factory per log instead.
  //
  // The correlation id comes from the lib: its onRequest mints a UUID
  // (crypto.randomUUID, with a v4 fallback for insecure contexts) and stamps it
  // on the x-correlation-id header itself. It also honours an id already on the
  // header, so nothing is lost if a caller sets one.
  //
  // The lib logs it under the header's own name. buildContext below re-exposes
  // it as `correlation_id` because that is the field InsightOps saved searches
  // and dashboards match on — read from meta rather than regenerated, so the
  // logged value cannot drift from the one on the wire.
  const telemetry = createTelemetryAxiosInterceptor(getLogger, {
    // Field names *and values* are kept verbatim so existing InsightOps saved
    // searches and dashboards keep matching: the lowercase axios method, and
    // api_end_point as the bare path this layer was called with (no baseURL,
    // DOWNLOAD_FILE prefix intact) rather than the absolute URL the lib builds.
    // buildContext is merged after the built-in fields, so it can override them.
    buildContext: ({ meta }) => ({
      correlation_id: meta[CORRELATION_ID_HEADER],
      user_id: readUserId(),
      method: meta.method.toLowerCase(),
      api_end_point:
        baseURL && meta.api_end_point.startsWith(baseURL)
          ? meta.api_end_point.slice(baseURL.length)
          : meta.api_end_point,
    }),
  });

  // Registered *after* the DOWNLOAD_FILE rewrite above, which means it runs
  // *before* it: axios walks request interceptors in reverse registration
  // order. That's deliberate — logging the url as the caller passed it
  // (DOWNLOAD_FILE prefix and all) is what the previous hand-rolled logging
  // recorded.
  axiosInstance.interceptors.request.use(telemetry.onRequest);
  axiosInstance.interceptors.response.use(telemetry.onResponse, (error) => {
    // Marked so handleRequest's catch can tell an already-reported failure from
    // one that never reached the interceptors at all (see below).
    if (error && typeof error === "object") {
      error.__telemetryLogged = true;
    }
    return telemetry.onError(error);
  });

  const handleError = (error) => {
    const { response, message } = error;
    if (response) {
      const { status, statusText, data } = response;
      switch (status) {
        // 401 is intentionally *not* handled here — the auth-lib interceptor
        // above already redirected to /logout by the time this catch runs.
        case 403:
          toast.error("You don't have permission to perform this action");
          globalThis.dispatchEvent(new Event("permissionRefetch"));
          return statusText;

        case 400:
        case 417:
          if (data) {
            return data;
          }
          break;

        case 500:
          toast.error(
            <div>
              <div>
                <b>Message:</b> {statusText}
              </div>
              <div>
                <b>Status code:</b> {status}
              </div>
              <div>{data.message || data.error || JSON.stringify(data)}</div>
            </div>
          );
          return statusText;

        default:
          toast.error(data?.message || statusText);
      }
    }
    return message;
  };

  /**
   * `source` tags the request with the FE surface that triggered it, as the X-Source header the
   * UAL backend resolves actions by (constants/ualActions.js). It is threaded explicitly the whole
   * way to axios because these wrappers take fixed positional parameters: an extra argument a
   * caller passes is silently discarded, which is how every UAL_SOURCES value was being dropped
   * while the call sites, index.js and the tests all looked correct.
   */
  const handleRequest = async (
    method,
    path,
    data,
    isFileUpload = false,
    source = undefined
  ) => {
    try {
      if (isFileUpload) {
        axiosInstance.defaults.headers = FILE_HEADERS;
      } else {
        axiosInstance.defaults.headers = HEADERS;
      }
      // Every log for this call — request, response and error — is emitted by
      // the telemetry interceptors registered above, including the
      // x-correlation-id header that used to be set here.
      const response = await axiosInstance.request({
        method,
        url: path,
        data,
        // Omitted entirely when absent, so untagged calls send no empty header.
        ...(source ? { headers: { "X-Source": source } } : {}),
      });
      return { ...response, body: response.data };
    } catch (error) {
      // Not every failure reaches the interceptors. A circular request body,
      // for one, exhausts the stack inside axios's own mergeConfig — before the
      // request chain is even assembled — so the rejection carries no config
      // and never passes through telemetry.onError. Such a call would be
      // invisible in InsightOps, which is exactly the class of bug these logs
      // exist to catch, so report it here. Errors the interceptor already
      // logged are skipped rather than logged twice.
      if (!error?.__telemetryLogged) {
        getLogger().error("api.error", {
          type: "Error",
          user_id: readUserId(),
          method,
          api_end_point: path,
          page_url: window.location.href,
          error_desc: error?.message ?? "API Error",
        });
      }
      throw handleError(error);
    }
  };

  const handleRequestForFile = async (method, path, data, source) => {
    return handleRequest(method, DOWNLOAD_FILE + path, data, false, source);
  };

  const handleRequestForFileUpload = async (method, path, data, source) => {
    return handleRequest(method, path, data, true, source);
  };

  return {
    // Raw axios instance — exposed for packages (e.g. @hoteltradergit/user-management)
    // that accept an AxiosInstance directly. Bearer token, X-App-ID and 401
    // redirect are added by createAuthAxiosInterceptor above, so consumers do
    // not re-wire auth.
    axiosInstance,
    getRequest: (path, source) =>
      handleRequest("get", path, undefined, false, source),
    postRequest: (path, data, source) =>
      handleRequest("post", path, data, false, source),
    putRequest: (path, data, source) =>
      handleRequest("put", path, data, false, source),
    delRequest: (path, source) =>
      handleRequest("delete", path, undefined, false, source),
    postFileRequest: (path, data, source) =>
      handleRequestForFile("post", path, data, source),
    postFileUploadRequest: (path, data, source) =>
      handleRequestForFileUpload("post", path, data, source),
    patchRequest: (path, data, source) =>
      handleRequest("patch", path, data, false, source),
    getFileRequest: (path, source) =>
      handleRequestForFile("get", path, undefined, source),
  };
};

export default createAxiosInstance;
